'use client';

import { useEffect, useState } from 'react';
import { useRouteHistory } from './useRouteHistory';

type RouteTrackerResult = {
  isFreshPageLoad: boolean; // Indicates if the page is loaded for the first time or window is reloaded
  isRoutePatternChanged: boolean;
};

/**
 * Custom hook to track route pattern changes and fresh page loads.
 * @param uniquePattern - unique substring to identify the route pattern (not strict match).
 */
export function useRouteTracker(uniquePattern: string): RouteTrackerResult {
  const { currentPath, previousPath } = useRouteHistory();
  const [isRoutePatternChanged, setIsRoutePatternChanged] =
    useState<boolean>(false);
  const [isFreshPageLoad, setIsFreshPageLoad] = useState<boolean>(false);

  useEffect(() => {
    if (currentPath === previousPath) return;
    setIsFreshPageLoad(previousPath === null);

    const isCurrentMatched = currentPath?.includes(uniquePattern) ?? false;
    const isPrevMatched = previousPath?.includes(uniquePattern) ?? false;
    setIsRoutePatternChanged(isCurrentMatched && !isPrevMatched);
  }, [currentPath, previousPath, uniquePattern]);

  return {
    isRoutePatternChanged,
    isFreshPageLoad,
  };
}
